// TGCLOUD-1813 — turn the fallback's invented placeholders into saveable people.
//
// THE DEFECT. A family with no stored pedigree configuration (every self-submitted
// FHQ family, 407 roots on dev) is drawn by `validatePedigreeConfig`, which derives
// the graph from the questionnaire's relationship labels and INVENTS a node for
// every person it needs but was never told about: the other parent of the
// proband's children when no spouse was named, and a half-sibling's missing
// parent(s). Those nodes are `{ pid: 'dummy-N', uid: 'dummy-N', isDummy: true }`.
// They rendered fine, but the first edit posted them wholesale to
// POST /pedigree-config, where `pid` is a Number column: the server had already
// claimed a UPN for the node and written the sibling rows when the cast failed,
// so every save 500'd, every retry burned another UPN, and set-relation's
// per-pid merge then seeded a 3-node config that on reopen replaced the label
// derivation and dropped every other relationship.
//
// The server now refuses a placeholder outright (400, before it mints or
// writes), so the client must never post one. This helper replaces each
// placeholder with a member record the ordinary save path can insert: a numeric
// pid (max + 1), a born-stable 24-hex uid (the same minting Add Relative uses —
// the backend upserts by `{_id: ObjectId(uid)}`, so the uid BECOMES the row's
// _id and the identity echo backfills upn/familyMemberId by pid), no `_id`, no
// `isDummy`, its gender and any name the derivation parsed. Every reference to
// the old 'dummy-N' is rewritten across every record, so the topology
// `validatePedigreeConfig` built (spouse symmetry, children back-pointers) is
// kept exactly, just under a real id.
//
// Pure, and it never mutates its input: the caller is the member-mapping effect
// in PedigreeChart, whose own header warns that a bug there silently corrupts
// the pedigree.

import { isPersistedMemberId, mintObjectIdHex } from './saveReconciliation';
import { allRelations } from 'src/components/questionnaire/Constants';

// Every field on a member record that holds another member's pid. Kept as one
// list so a new reference field is added here, not discovered by a stale
// 'dummy-N' surviving into a save.
const SCALAR_REF_FIELDS = [
  'fatherId',
  'motherId',
  'bioFatherId',
  'bioMotherId',
  // Scalar written by SetRelation alongside spouseIds.
  'spouse'
];
const ARRAY_REF_FIELDS = [
  'spouseIds',
  'childrenIds',
  'siblingIds',
  'crossLevelSpouseIds',
  'proxyIds',
  'donorOfPids'
];

// The server's rule for a postable pid, mirrored (backend/util/
// pedigreeConfigPost.js): Number(v) is finite — a Number, or a numeric string
// such as '12'. 'dummy-1' fails it, and so does the 24-hex string a member
// fell back to when it had no UPN to parse (validatePedigreeConfig uses
// `m.pid || m.uid || m._id`). null, '' and booleans are not pids.
export const isFinitePid = (pid) => {
  if (typeof pid === 'number') return Number.isFinite(pid);
  if (typeof pid === 'string') {
    return pid.trim() !== '' && Number.isFinite(Number(pid));
  }
  return false;
};

// Anything the save path cannot post as it is: an invented node, or a record
// whose pid is not numeric. The second kind splits again below.
const isPlaceholder = (r) => r?.isDummy === true || !isFinitePid(r?.pid);

// A record that already exists on the server. Such a record must keep its
// identity even when its pid is unusable — re-minting its uid would make the
// save INSERT a second row for a real person (the backend upserts by uid).
const hasPersistedIdentity = (r) =>
  isPersistedMemberId(r?._id) ||
  !!r?.familyMemberId ||
  isPersistedMemberId(r?.uid);

// A person who does not exist on the server at all — the only kind that gets
// a minted uid. A real member with a hex pid is NOT one of these: it is
// renumbered and keeps its identity, and a reference to it resolves to that
// identity (placeholderSignature below).
const isInventedPerson = (r) =>
  r?.isDummy === true || (!isFinitePid(r?.pid) && !hasPersistedIdentity(r));

const findProband = (records) =>
  records.find((r) => r?.pid === 1) ||
  records.find((r) => r?.isProband === true) ||
  records.find((r) => r?.proBandId != null);

const isHalfSibling = (r) =>
  r?.relationship === allRelations.halfBrother ||
  r?.relationship === allRelations.halfSister;

// What the derivation invented a placeholder FOR. Decision D1: the proband's
// unnamed partner is labelled `spouse`, and a half-sibling's invented parent —
// either half of the couple a half-sibling with no shared parent gets, or the
// unresolved other parent next to a shared one — is labelled the way the
// backend's `resolveOtherParent` labels it (HALF_SIBLING_PARENT_RELATIONSHIP,
// the `allRelations.halfSiblingParent` key). The label is load-bearing for the
// partner: the next run of the label derivation sees a `spouse` and stops
// inventing one. Null for anything else.
export function placeholderRole(record, records) {
  const list = Array.isArray(records) ? records : [];
  const pid = String(record?.pid);
  const proband = findProband(list);
  const probandSpouses = Array.isArray(proband?.spouseIds)
    ? proband.spouseIds
    : [];
  if (probandSpouses.some((id) => String(id) === pid)) {
    return allRelations.spouse;
  }
  const parentsAHalfSibling = list.some(
    (r) =>
      isHalfSibling(r) &&
      (String(r.fatherId) === pid || String(r.motherId) === pid)
  );
  if (parentsAHalfSibling) return allRelations.halfSiblingParent;
  return null;
}

// What a placeholder IS, independent of the 'dummy-N' counter: its gender, its
// role, the real children it was invented for, the real spouse it was paired
// with and any name parsed from a free-text other-parent. The counter is NOT
// stable across runs of the fallback — once the proband's partner has been
// saved and the family re-read, the label derivation no longer invents it, so
// the half-sibling's parent that was 'dummy-2' becomes 'dummy-1'. A caller
// that wants to hand the same placeholder the same uid on every run keys on
// this.
//
// Every reference is resolved THROUGH `records` to the referenced record's
// stable identity (its uid, else _id, else the pid string). Keying on the
// referenced pids themselves was the defect: a member with no parseable UPN
// carries its 24-hex _id as pid, so such references were dropped from the key
// and two same-gender placeholders — the two unnamed fathers of two
// half-siblings, say — collapsed to ONE signature, were handed ONE uid, and
// the save folded two people into one row. A referenced placeholder (the other
// half of an invented couple) contributes its gender only, never its counter.
export function placeholderSignature(record, records = []) {
  const byPid = new Map();
  (Array.isArray(records) ? records : []).forEach((r) => {
    if (r?.pid !== null && r?.pid !== undefined) byPid.set(String(r.pid), r);
  });
  const stableRef = (ref) => {
    const target = byPid.get(String(ref));
    if (!target) return `?${String(ref)}`;
    if (isInventedPerson(target)) return `ph:${target.gender ?? ''}`;
    return String(target.uid ?? target._id ?? target.pid);
  };
  const refs = (arr) =>
    (Array.isArray(arr) ? arr : []).map(stableRef).sort().join(',');
  const name = `${record?.firstName ?? ''} ${record?.lastName ?? ''}`.trim();
  return [
    record?.gender ?? '',
    `r:${placeholderRole(record, records) ?? ''}`,
    `c:${refs(record?.childrenIds)}`,
    `s:${refs(record?.spouseIds)}`,
    `n:${name}`
  ].join('|');
}

const dedupe = (arr) => {
  const seen = new Set();
  return arr.filter((v) => {
    const key = String(v);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * @param {object[]} records - `validatePedigreeConfig` output (or any member
 *   record array).
 * @param {object} [options]
 * @param {(placeholder: object, context: {records: object[], role: string|null, attempt: number}) => string} [options.mintUid]
 *   Returns the uid for a placeholder. Default: a fresh 24-hex ObjectId
 *   string. Receives the placeholder record and a context — the full record
 *   array (so the caller can key on `placeholderSignature(placeholder,
 *   records)` for a STABLE uid across runs), the role D1 assigns, and
 *   `attempt`, which is 0 on the first call and 1 if the uid returned was
 *   already handed to another placeholder in this run. A caller keying on the
 *   signature must fold `attempt` into its key so the retry is stable too.
 *   If the uid returned already belongs to a real record in `records` — the
 *   placeholder was saved on an earlier run and has since been re-read as a
 *   member — the placeholder is ABSORBED into that record: its links are
 *   merged in and every reference is rewritten to the real pid, so a re-run
 *   never produces a second copy.
 * @returns {{ records: object[], materialised: Array<{oldPid: *, pid: number, uid: string, kind: 'new'|'absorb'|'renumber'}> }}
 *   `records` is the SAME array reference when nothing was a placeholder.
 *   `kind` says what happened: `new` is a person who does not exist on the
 *   server yet (the only kind an automatic save exists for); `absorb` folded
 *   the placeholder into a member that already exists; `renumber` gave a REAL
 *   member with an unusable pid a numeric one and kept its identity.
 * @throws when two placeholders would end up sharing one uid even after the
 *   retry — two people must never fold into one row.
 */
export function materialisePlaceholders(records, { mintUid } = {}) {
  if (!Array.isArray(records)) return { records, materialised: [] };
  const placeholders = records.filter(isPlaceholder);
  if (placeholders.length === 0) return { records, materialised: [] };

  const mint = typeof mintUid === 'function' ? mintUid : mintObjectIdHex;

  let nextPid = records.reduce(
    (max, r) =>
      isFinitePid(r?.pid) && Number(r.pid) > max ? Number(r.pid) : max,
    0
  );
  const realByUid = new Map();
  records.forEach((r) => {
    if (!isPlaceholder(r) && r?.uid != null) realByUid.set(String(r.uid), r);
  });

  // Every uid a placeholder has resolved to in THIS run, plus the uids of the
  // renumbered real members (which stay in the output under their own uid).
  // A second placeholder landing on any of them would mean two people in one
  // row — the minter gets one retry with a disambiguating `attempt`, and if
  // that collides as well the run refuses rather than folding them.
  const assigned = new Set();
  const takeUid = (r, role) => {
    let uid = String(mint(r, { records, role, attempt: 0 }));
    if (assigned.has(uid)) {
      uid = String(mint(r, { records, role, attempt: 1 }));
      if (assigned.has(uid)) {
        throw new Error(
          `materialisePlaceholders: two placeholders resolved to the same uid ${uid} (TGCLOUD-1813)`
        );
      }
    }
    assigned.add(uid);
    return uid;
  };

  // Pass 1 — decide every placeholder's destination before rewriting anything,
  // because a placeholder can reference another placeholder (the half-sibling
  // dummy couple are each other's spouse) and both ends must resolve.
  const remap = new Map(); // String(oldPid) -> destination pid
  const plans = new Map(); // String(oldPid) -> { kind, pid, uid, role }
  const materialised = [];
  for (const r of placeholders) {
    const key = String(r.pid);
    if (r.isDummy !== true && hasPersistedIdentity(r)) {
      // A real member whose pid is unusable: renumber, keep its identity.
      const pid = ++nextPid;
      const uid = r.uid != null ? String(r.uid) : r.uid;
      if (uid != null) assigned.add(uid);
      plans.set(key, { kind: 'renumber', pid, uid: r.uid });
      remap.set(key, pid);
      materialised.push({ oldPid: r.pid, pid, uid: r.uid, kind: 'renumber' });
      continue;
    }
    const role = placeholderRole(r, records);
    const uid = takeUid(r, role);
    const existing = realByUid.get(uid);
    if (existing) {
      plans.set(key, { kind: 'absorb', pid: existing.pid, uid, role });
      remap.set(key, existing.pid);
      materialised.push({
        oldPid: r.pid,
        pid: existing.pid,
        uid,
        kind: 'absorb'
      });
      continue;
    }
    const pid = ++nextPid;
    plans.set(key, { kind: 'new', pid, uid, role });
    remap.set(key, pid);
    materialised.push({ oldPid: r.pid, pid, uid, kind: 'new' });
  }

  const rewrite = (v) => {
    if (v === null || v === undefined) return v;
    const hit = remap.get(String(v));
    return hit === undefined ? v : hit;
  };

  // Returns the same object when no reference changed, so untouched members
  // keep their identity for anything keyed on it.
  const rewriteLinks = (r) => {
    const patch = {};
    for (const f of SCALAR_REF_FIELDS) {
      if (r[f] === null || r[f] === undefined) continue;
      const nv = rewrite(r[f]);
      if (nv !== r[f]) patch[f] = nv;
    }
    for (const f of ARRAY_REF_FIELDS) {
      if (!Array.isArray(r[f])) continue;
      let changed = false;
      const nv = r[f].map((v) => {
        const w = rewrite(v);
        if (w !== v) changed = true;
        return w;
      });
      if (changed) patch[f] = dedupe(nv);
    }
    if (Array.isArray(r.twinRelations)) {
      let changed = false;
      const nv = r.twinRelations.map((rel) => {
        if (!rel || rel.withPid === null || rel.withPid === undefined)
          return rel;
        const w = rewrite(rel.withPid);
        if (w === rel.withPid) return rel;
        changed = true;
        return { ...rel, withPid: w };
      });
      if (changed) patch.twinRelations = nv;
    }
    return Object.keys(patch).length ? { ...r, ...patch } : r;
  };

  // Pass 2 — build the output: placeholders become members (or fold into the
  // member they already are), everything else passes through.
  const absorbedLinks = new Map(); // String(target pid) -> links to union in
  const out = [];
  for (const r of records) {
    if (!isPlaceholder(r)) {
      out.push(r);
      continue;
    }
    const plan = plans.get(String(r.pid));
    if (plan.kind === 'absorb') {
      const rewritten = rewriteLinks(r);
      const acc = absorbedLinks.get(String(plan.pid)) || {};
      for (const f of ARRAY_REF_FIELDS) {
        if (!Array.isArray(rewritten[f]) || rewritten[f].length === 0) continue;
        acc[f] = [...(acc[f] || []), ...rewritten[f]];
      }
      absorbedLinks.set(String(plan.pid), acc);
      continue;
    }
    if (plan.kind === 'renumber') {
      out.push({ ...r, pid: plan.pid });
      continue;
    }
    const next = { ...r, pid: plan.pid, uid: plan.uid };
    delete next.isDummy;
    delete next._id;
    // D1: the invented person is labelled the way the backend's own
    // materialisation labels it — `spouse` for the proband's partner, and
    // `halfSiblingParent` for a half-sibling's invented parent(s) — so the
    // family listing, exports and the next run of the label derivation treat
    // it as the person it stands for instead of inventing another one. A
    // label the derivation already gave it (a named other-parent carries
    // none today) is left alone.
    if (
      (next.relationship === null || next.relationship === undefined) &&
      plan.role
    ) {
      next.relationship = plan.role;
    }
    out.push(next);
  }

  const result = out.map((r) => {
    const rewritten = rewriteLinks(r);
    const extra = absorbedLinks.get(String(rewritten.pid));
    if (!extra) return rewritten;
    const patch = {};
    for (const f of ARRAY_REF_FIELDS) {
      if (!extra[f]) continue;
      patch[f] = dedupe([
        ...(Array.isArray(rewritten[f]) ? rewritten[f] : []),
        ...extra[f]
      ]).filter((v) => String(v) !== String(rewritten.pid));
    }
    return { ...rewritten, ...patch };
  });

  return { records: result, materialised };
}

/**
 * Whether a run of materialisePlaceholders produced a person the server has
 * never seen. ONLY a `kind: 'new'` entry counts, and only while its record
 * has no familyMemberId (the caller may have carried one over from a live
 * record whose earlier save landed). A `renumber` is a real member that
 * merely lacked a usable pid and an `absorb` folded into a member that
 * already exists — neither is a reason to commit a clinical save with no
 * user gesture, which is what the caller uses this to decide.
 */
export function hasUnsavedMaterialised(materialised, records) {
  const newUids = new Set(
    (Array.isArray(materialised) ? materialised : [])
      .filter((m) => m?.kind === 'new')
      .map((m) => String(m.uid))
  );
  if (newUids.size === 0) return false;
  return (Array.isArray(records) ? records : []).some(
    (r) => newUids.has(String(r?.uid)) && !r?.familyMemberId
  );
}

/**
 * Decision D3, as one pure rule: may the fallback commit the members it just
 * materialised WITHOUT a user gesture?
 *
 * @returns {{arm: boolean, defer: boolean}} `arm` — set save -> IN_PROGRESS
 *   now. `defer` — the answer is not knowable yet (the family lock has not
 *   been read); the caller records the intent and asks again once it is.
 *   Never both.
 *
 *   unsaved:          nothing to commit -> nothing to decide.
 *   configReadFailed: a FAILED config read must never auto-write a derived
 *                     topology over a real one. PedigreeBox applies `null`
 *                     for an error exactly as for a genuine "nothing stored"
 *                     (TGCLOUD-1804), so without this a transient 500 on the
 *                     GET would overwrite a configured family's graph with
 *                     the label derivation, unprompted.
 *   preview:          read-only, no edit surface, nothing may be written.
 *   personMemberId:   a person tab mounts this chart hidden. Arming there
 *                     would take the FAMILY lock and mark the tab edited from
 *                     a tab that shows one person's record, not the pedigree.
 *   lockResolved:     usePedigreeLock starts with lockState null and fills it
 *                     only after the socket connects and the first acquire /
 *                     GET SUCCEEDS (a failed read leaves it null and this
 *                     flag false), so isLocked is false on first render
 *                     whoever holds the family. Deciding on that would either
 *                     raise the "not saved" notice on mere open (lock lands
 *                     inside the debounce) or write into a family another
 *                     clinician holds (it lands later) — the config POST has
 *                     no server-side lock check. So: not knowable yet.
 *   isLocked:         another clinician holds the family; the save would be
 *                     refused and the refusal would be a false alarm.
 */
export function decidePlaceholderAutoSave({
  unsaved,
  configReadFailed,
  isLocked,
  lockResolved,
  preview,
  personMemberId
}) {
  const no = { arm: false, defer: false };
  if (!unsaved) return no;
  if (configReadFailed) return no;
  if (preview) return no;
  if (personMemberId) return no;
  if (!lockResolved) return { arm: false, defer: true };
  if (isLocked) return no;
  return { arm: true, defer: false };
}
